'use client'

import { useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { ProjectCard } from '@/components/projects/ProjectCard'
import { EmptyState } from '../common/EmptyState'
import type { ProjectCard as ProjectCardType } from '@/types/dashboard'
import { BriefcaseBusiness } from 'lucide-react'

type TabKey = 'requesting' | 'reviewing' | 'active'

interface CompanyProjectsTabsProps {
  projects: {
    requesting: ProjectCardType[]
    reviewing: ProjectCardType[]
    active: ProjectCardType[]
  }
}

export function CompanyProjectsTabs({ projects }: CompanyProjectsTabsProps) {
  const router = useRouter()
  const [activeTab, setActiveTab] = useState<TabKey>('requesting')

  const tabs = useMemo(
    () => [
      { key: 'requesting' as const, label: '견적 기다리는 중', count: projects.requesting.length },
      { key: 'reviewing' as const, label: '견적 비교 중', count: projects.reviewing.length },
      { key: 'active' as const, label: '진행 중', count: projects.active.length },
    ],
    [projects.requesting.length, projects.reviewing.length, projects.active.length]
  )

  const currentProjects = projects[activeTab]
  const activeLabel = tabs.find((t) => t.key === activeTab)?.label ?? ''

  const emptyCopy = useMemo(() => {
    if (activeTab === 'requesting') return { title: '요청중 프로젝트가 없습니다', desc: '새 프로젝트를 등록해보세요.' }
    if (activeTab === 'reviewing') return { title: '검토중 프로젝트가 없습니다', desc: '견적이 도착하면 여기에 표시됩니다.' }
    return { title: '진행중 프로젝트가 없습니다', desc: '진행중인 프로젝트가 없습니다.' }
  }, [activeTab])

  return (
    <div className="space-y-4">
      {/* Tabs */}
      <div className="flex items-center justify-between">
        <div
          className={[
            'flex gap-2',
            'overflow-x-auto no-scrollbar',
            '-mx-1 px-1', // ✅ 모바일 스크롤 여백
          ].join(' ')}
        >
          {tabs.map((tab) => {
            const isActive = activeTab === tab.key
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={[
                  'inline-flex items-center gap-2',
                  'h-10 px-4 rounded-full',
                  'text-sm font-semibold',
                  'border transition-all',
                  'whitespace-nowrap',
                  isActive
                    ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                    : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50',
                ].join(' ')}
              >
                <span>{tab.label}</span>
                {tab.count > 0 && (
                  <span
                    className={[
                      'inline-flex items-center justify-center',
                      'min-w-[22px] h-[22px] px-2 rounded-full',
                      'text-xs font-bold',
                      isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-700',
                    ].join(' ')}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            )
          })}
        </div>

        {/* optional: active tab label (desktop helper) */}
        <div className="hidden sm:block text-sm text-gray-500">
          {activeLabel} <span className="text-gray-400">·</span>{' '}
          <span className="font-semibold text-gray-900">{currentProjects.length}</span>
        </div>
      </div>

      {/* List */}
      {currentProjects.length === 0 ? (
        <EmptyState
          icon={BriefcaseBusiness}
          title={emptyCopy.title}
          description={emptyCopy.desc}
          cta={activeTab === 'requesting' ? { label: '프로젝트 등록하기', href: '/client/projects/new' } : undefined}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {currentProjects.map((item) => {
            // ✅ 핵심: 대시보드 카드(item) 자체가 아니라,
            // 백엔드에서 내려준 item.project(= ProjectCardModel 호환 payload)를 ProjectCard에 전달
            const cardModel = (item as any).project
            const href = (item as any).href as string | undefined

            if (!cardModel) {
              // 방어 로직: API/타입 불일치 시에도 렌더 크래시 방지
              return null
            }

            return (
              <ProjectCard
                key={item.id}
                project={cardModel}
                mode="companyMyProject"
                onClick={() => router.push(href ?? `/client/projects/${item.id}`)}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}
